import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { purchasesEnabled, NATIVE_BILLING_NOTICE } from "@/lib/billing/purchaseGate";
import { useAuth } from "@/contexts/AuthContext";
import { logAuthEvent } from "@/lib/debug/authEventLog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ShieldAlert, ExternalLink, LogOut } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const LoadingScreen = () => (
  <div className="min-h-screen bg-lr-dark flex items-center justify-center">
    <div className="text-white">Loading...</div>
  </div>
);

const destinationForRole = (userType: string | null | undefined): string => {
  switch (userType) {
    case "broker":
    case "broker_carrier":
      return "/broker-dashboard";
    case "carrier":
      return "/carrier-dashboard";
    case "shipper":
      return "/lr-direct/my-opportunities";
    default:
      return "/";
  }
};

export default function ReactivatePage() {
  const { user, loading, profile, hasActiveSubscription } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [billingLoading, setBillingLoading] = useState(false);

  // 1. Auth still hydrating — show loading screen.
  if (loading) {
    return <LoadingScreen />;
  }

  // 2. Logged out — push to login (replace, no history entry).
  if (!user) {
    return <Navigate to="/auth?mode=login" replace />;
  }

  // 3. Logged in but profile not yet loaded — treat as hydration.
  //    hasActiveSubscription is derived from profile + company sub; without
  //    profile we cannot trust the `false` default.
  if (!profile) {
    return <LoadingScreen />;
  }

  // 4. Active subscriber landed here by mistake — bounce them away (replace).
  if (hasActiveSubscription) {
    return <Navigate to={destinationForRole(profile.user_type)} replace />;
  }

  // 5. Truly inactive/canceled logged-in user — render recovery UI.
  const handleManageSubscription = async () => {
    if (!user) return;
    setBillingLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "create-billing-portal-session",
        { body: { user_id: user.id } }
      );
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Could not open billing portal.",
        variant: "destructive",
      });
    } finally {
      setBillingLoading(false);
    }
  };

  const handleStartFresh = async () => {
    if (!user) return;
    setBillingLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "create-reactivation-checkout",
        { body: {} }
      );
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Could not start subscription.",
        variant: "destructive",
      });
    } finally {
      setBillingLoading(false);
    }
  };

  const handleSignOut = async () => {
    // scope: 'local' — this device only. Bare signOut() defaults to global
    // scope, which would revoke the user's session on every other device.
    logAuthEvent('explicit_signout', { call_site: 'ReactivatePage.handleSignOut', scope: 'local' }, user?.id ?? null);
    await supabase.auth.signOut({ scope: 'local' });
    navigate("/auth?mode=login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-lr-dark flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8 text-center space-y-6 bg-card border-border">
        <div className="flex justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
            <ShieldAlert className="h-7 w-7 text-destructive" />
          </div>
        </div>

        <div className="space-y-2">
          <h1 className="text-xl font-bold text-foreground">
            Your subscription is no longer active
          </h1>
          <p className="text-sm text-muted-foreground">
            {purchasesEnabled()
              ? "Reactivate to continue using LaneRunner. Start a fresh subscription (no trial — billed immediately) or manage your existing billing in Stripe."
              : "Contact support at support@lanerunner.com for help with your account."}
          </p>
        </div>

        <div className="space-y-3">
          {!purchasesEnabled() ? (
            <p className="text-sm text-muted-foreground">{NATIVE_BILLING_NOTICE}</p>
          ) : (
          <>
          <Button
            onClick={handleStartFresh}
            disabled={billingLoading}
            className="w-full"
          >
            {billingLoading ? "Opening…" : "Start Fresh Subscription"}
          </Button>

          <Button
            onClick={handleManageSubscription}
            disabled={billingLoading}
            variant="outline"
            className="w-full"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            {billingLoading ? "Opening…" : "Manage Subscription"}
          </Button>
          </>
          )}

          <button
            onClick={handleSignOut}
            className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mx-auto"
          >
            <LogOut className="h-3.5 w-3.5" />
            Sign out
          </button>
        </div>
      </Card>
    </div>
  );
}
